<template>
  <div class="border-b shrink-0" style="background: #FFFFFF; border-color: #E8E9E9;">
    <!-- Upper strip: brand + identity + tenant switcher + viewer -->
    <div class="h-14 flex items-center px-6 gap-4">
      <!-- Brand mark + label -->
      <div class="flex items-center gap-2.5">
        <img :src="brand.logo" alt="Kairon" class="w-8 h-8 rounded" />
        <div>
          <div class="text-[13px] font-semibold leading-tight text-gray-900">{{ brand.short }}</div>
          <div class="text-[10px] uppercase tracking-wider leading-tight text-gray-500">Performance Share</div>
        </div>
      </div>

      <div class="w-px h-6 bg-gray-200" />

      <!-- Tenant switcher (only if user has multiple tenants) -->
      <div v-if="hasMultipleTenants" class="relative">
        <button
          type="button"
          class="flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-50"
          @click="tenantDropdownOpen = !tenantDropdownOpen; entityDropdownOpen = false"
        >
          <span class="sensitive" data-spoof-kind="tenant">{{ current_tenant_name }}</span>
          <font-awesome-icon :icon="['fas', 'chevron-down']" class="w-2.5 h-2.5" />
        </button>
        <div v-if="tenantDropdownOpen" class="absolute left-0 top-full mt-1 z-50">
          <Dropdown_Selector_Single
            spoof-kind="tenant"
            :options="availableTenants"
            :sensitive-labels="true"
            @close="tenantDropdownOpen = false"
            @select="onTenantChange"
          />
        </div>
      </div>

      <!-- Entity picker (only if user has multiple entities) -->
      <div v-if="hasMultipleEntities" class="relative ml-2">
        <button
          type="button"
          class="flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-50"
          @click="entityDropdownOpen = !entityDropdownOpen; tenantDropdownOpen = false"
        >
          <font-awesome-icon :icon="['fas', 'hospital']" class="w-3 h-3" />
          <span>{{ currentEntityLabel }}</span>
          <font-awesome-icon :icon="['fas', 'chevron-down']" class="w-2.5 h-2.5" />
        </button>
        <div v-if="entityDropdownOpen" class="absolute left-0 top-full mt-1 z-50">
          <Dropdown_Selector_Single
            :options="entityOptions"
            :sensitive-labels="true"
            @close="entityDropdownOpen = false"
            @select="onEntityChange"
          />
        </div>
      </div>

      <div class="flex-1" />

      <!-- Sanitized indicator (stub) -->
      <div class="hidden md:flex items-center gap-1.5 text-[11px] text-gray-500">
        <font-awesome-icon :icon="['fas', 'lock']" class="w-3 h-3" />
        <span>Sanitized</span>
      </div>

      <button
        v-if="!isExternalOnly"
        type="button"
        class="flex items-center gap-1.5 text-[12px] font-medium text-gray-700 px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50"
        @click="$router.push({ name: 'Home' })"
      >
        <font-awesome-icon :icon="['fas', 'arrow-left']" class="w-3 h-3" />
        <span>Back to Kairon</span>
      </button>

      <!-- Viewer -->
      <div class="flex items-center gap-2 pl-2">
        <div class="w-8 h-8 rounded-full bg-neutral-900 text-white flex items-center justify-center text-[11px] font-semibold sensitive">
          {{ viewerInitials }}
        </div>
        <div class="hidden lg:block">
          <div class="text-[12px] font-medium leading-tight text-gray-900 sensitive">{{ viewerName }}</div>
          <div class="text-[10px] leading-tight text-gray-500">Signed in</div>
        </div>
      </div>
    </div>

    <!-- Primary nav tabs -->
    <nav class="px-6 flex items-center gap-0 -mb-px">
      <button
        v-for="item in navItems"
        :key="item.id"
        class="px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors flex items-center gap-2"
        :class="activeId === item.id
          ? 'border-primary-500 text-gray-900'
          : 'border-transparent text-gray-500 hover:text-gray-800'"
        @click="$emit('change', item.id)"
      >
        <font-awesome-icon :icon="['fas', item.icon]" class="w-3.5 h-3.5" />
        <span class="sensitive" data-spoof-kind="label">{{ item.label }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Dropdown_Selector_Single from '@/components/widgetcomponents/Dropdown_Selector/Dropdown_Selector_Single.vue';
import kaironLogo from '@/assets/kairon_logo_orange.svg';
import { spoofText } from '@/utils/demoSpoof';

export default {
  name: 'ClientTopBar',
  components: { Dropdown_Selector_Single },
  props: {
    activeId: { type: String, default: 'overview' },
    navItems: { type: Array, default: () => [] },
  },
  emits: ['change'],
  data() {
    return {
      tenantDropdownOpen: false,
      entityDropdownOpen: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', [
      'availableTenants',
      'current_tenant_name',
      'current_tenant_slug',
      'isExternalOnly',
    ]),
    ...mapGetters('entity', ['availableEntities', 'current_entity_id']),
    hasMultipleTenants() {
      return this.availableTenants && this.availableTenants.length > 1;
    },
    hasMultipleEntities() {
      return this.availableEntities && this.availableEntities.length > 1;
    },
    entityOptions() {
      return (this.availableEntities || []).map(e => ({
        id: e.entity_id,
        entity_id: e.entity_id,
        name: this.$root?.isDemoMode ? spoofText(e.name) : e.name,
      }));
    },
    currentEntityLabel() {
      const match = (this.availableEntities || []).find(
        e => Number(e.entity_id) === Number(this.$route.params.entityid)
      );
      const name = match ? match.name : 'Select practice';
      return this.$root?.isDemoMode ? spoofText(name) : name;
    },
    brand() {
      return {
        primary: '#F76734',
        logo: kaironLogo,
        short: 'Kairon',
      };
    },
    viewerName() {
      const u = this.current_user;
      if (!u) return '';
      return u.first_name && u.last_name
        ? `${u.first_name} ${u.last_name}`
        : u.email || '';
    },
    viewerInitials() {
      const u = this.current_user;
      if (!u) return '';
      if (u.first_name && u.last_name) {
        return (u.first_name[0] + u.last_name[0]).toUpperCase();
      }
      return (u.email || '?')[0].toUpperCase();
    },
  },
  methods: {
    ...mapActions('tenant', ['setTenant_slug']),
    ...mapActions('entity', ['setEntity_id']),
    onTenantChange(tenant) {
      if (!tenant.slug || tenant.slug === this.current_tenant_slug) {
        this.tenantDropdownOpen = false;
        return;
      }
      this.setTenant_slug(tenant.slug);
      window.location.href = '/';
    },
    onEntityChange(entity) {
      this.entityDropdownOpen = false;
      if (!entity.entity_id || Number(entity.entity_id) === Number(this.$route.params.entityid)) {
        return;
      }
      this.setEntity_id(entity.entity_id);
      this.$router.push({
        name: 'ExternalEntity',
        params: { entityid: entity.entity_id },
      });
    },
  },
};
</script>
